/**
 * CheckboxField Component - Checkbox input with label
 */

import React, { useCallback } from 'react';
import { Check, Minus } from 'lucide-react';
import { CheckboxFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  name,
  label,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  checked,
  indeterminate = false,
  children,
  onChange,
  onBlur,
  onFocus,
}) => {
  const fieldId = `field-${name}`;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled && !readonly) {
        onChange?.(e.target.checked);
      }
    },
    [disabled, readonly, onChange]
  );

  const checkboxClasses = `
    relative w-5 h-5
    border-2 rounded
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
    ${checked || indeterminate ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'}
    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
    ${error ? 'border-red-300' : ''}
    transition-colors duration-200
  `;

  // For standalone checkbox with label
  if (label && !children) {
    return (
      <BaseField
        name={name}
        helpText={helpText}
        required={required}
        disabled={disabled}
        readonly={readonly}
        error={error}
        className={className}
        fieldId={fieldId}
      >
        <label
          htmlFor={fieldId}
          className={`flex items-start ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <div className="relative flex items-center">
            <input
              id={fieldId}
              name={name}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              readOnly={readonly}
              required={required}
              className="sr-only"
              onChange={handleChange}
              onBlur={onBlur}
              onFocus={onFocus}
              aria-invalid={!!error}
              aria-describedby={error ? `${fieldId}-error` : undefined}
            />
            <div className={checkboxClasses}>
              {indeterminate && (
                <Minus className="absolute inset-0 w-3 h-3 m-auto text-white" />
              )}
              {checked && !indeterminate && (
                <Check className="absolute inset-0 w-3 h-3 m-auto text-white" />
              )}
            </div>
          </div>
          <span className="ml-2 text-sm text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </label>
      </BaseField>
    );
  }

  // For checkbox with custom children content
  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div className="flex items-start">
        <div className="relative flex items-center">
          <input
            id={fieldId}
            name={name}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            readOnly={readonly}
            required={required}
            className="sr-only"
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-invalid={!!error}
            aria-describedby={error ? `${fieldId}-error` : undefined}
          />
          <div className={checkboxClasses}>
            {indeterminate && (
              <Minus className="absolute inset-0 w-3 h-3 m-auto text-white" />
            )}
            {checked && !indeterminate && (
              <Check className="absolute inset-0 w-3 h-3 m-auto text-white" />
            )}
          </div>
        </div>
        {children && (
          <label
            htmlFor={fieldId}
            className={`ml-2 text-sm ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          >
            {children}
          </label>
        )}
      </div>
    </BaseField>
  );
};

// Checkbox group component for multiple checkboxes
interface CheckboxGroupProps {
  name: string;
  label?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  value: string[];
  onChange: (values: string[]) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  orientation?: 'horizontal' | 'vertical';
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  name,
  label,
  options,
  value = [],
  onChange,
  required,
  disabled,
  error,
  helpText,
  orientation = 'vertical',
}) => {
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const newValues = checked
      ? [...value, optionValue]
      : value.filter((v) => v !== optionValue);
    onChange(newValues);
  };

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      error={error}
    >
      <div
        className={`flex ${
          orientation === 'horizontal' ? 'flex-row gap-4' : 'flex-col gap-2'
        }`}
        role="group"
        aria-labelledby={label ? `${name}-label` : undefined}
      >
        {options.map((option) => (
          <CheckboxField
            key={option.value}
            name={`${name}-${option.value}`}
            checked={value.includes(option.value)}
            disabled={disabled || option.disabled}
            onChange={(checked) => handleCheckboxChange(option.value, checked)}
          >
            {option.label}
          </CheckboxField>
        ))}
      </div>
    </BaseField>
  );
};

export default CheckboxField;